import React, { useCallback, useEffect, useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { X, Send } from "lucide-react";
import decodeToken from "../../lib/decodeToken";
import { getTicketChats, sendTicketChat } from "../../lib/needHelpApi";
import styles from "./NeedHelpTickets.module.css";

const getToken = () => {
  try {
    return localStorage.getItem("token") || "";
  } catch (_e) {
    return "";
  }
};

const formatTime = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MAX_LEN = 2000;


// ADDED: iPhone / iPad (iPadOS reports itself as a Mac with a touchscreen).
const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
};
// ADDED: taller popup while the keyboard is open, iPhone / iPad only.
// Android keeps the two values above.
// Popup height = visible height - IOS_KEYBOARD_TOP_GAP + IOS_KEYBOARD_EXTRA_BOTTOM
const IOS_KEYBOARD_TOP_GAP = 8;
const IOS_KEYBOARD_EXTRA_BOTTOM = -15;
// ADDED: overlay padding (16 top + 16 bottom) + close button (54) + gap (12).
// The popup is never taller than the visible area minus this.
const CHROME_HEIGHT = 98;

// ADDED: the area the user can really see right now. On iPhone Safari the
// "vh" / "dvh" units can be taller than this (address bar, toolbar), which is
// what pushed the top of the popup off screen.
const readViewport = () => {
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  if (vv) return { height: vv.height, top: vv.offsetTop };
  return { height: window.innerHeight, top: 0 };
};

// Chat popup for one ticket.
// Each message: { sentBy: "user" | "admin", message, isRead, timestamp }
// `onRead` is called once the chat was loaded: the backend has then marked the
// admin's messages as read, so the unread dot can go away.
const NeedHelpTicketChat = ({ ticket, onClose, onRead }) => {
  const userId = decodeToken(getToken())?.id || "";
  const listRef = useRef(null);
  const inputRef = useRef(null);
  // Used to block scrolling of everything behind the popup.
  const overlayRef = useRef(null);
  // ADDED: biggest visible height seen since the popup opened (keyboard closed).
  // A visible area much smaller than this means the keyboard is open, even
  // when iPhone Safari does not change window.innerHeight.
  const baseRef = useRef({ height: 0, width: 0 });

  // Kept in a ref so a new function from the parent never reloads the chat.
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;

  const [chats, setChats] = useState([]);
  // "loading" | "ready" | "error"
  const [status, setStatus] = useState("loading");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  // CHANGED: the visible area is always tracked (not only with the keyboard
  // open), and the keyboard state is its own flag.
  const [view, setView] = useState(readViewport);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  const loadChats = useCallback(async () => {
    setStatus("loading");
    try {
      const list = await getTicketChats({
        ticketId: ticket.ticketId,
        userId,
        token: getToken(),
      });
      setChats(list);
      setStatus("ready");
      onReadRef.current?.();
    } catch (_e) {
      setStatus("error");
    }
  }, [ticket.ticketId, userId]);

  // Hit the API every time the popup opens.
  useEffect(() => {
    loadChats();
  }, [loadChats]);

  // Keep the newest message in view.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chats.length, status, view.height]);

  // Message box: 1 line to start, grows with the text up to 3 lines, then scrolls.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight) || 22;
    const padding =
      (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const border =
      (parseFloat(cs.borderTopWidth) || 0) +
      (parseFloat(cs.borderBottomWidth) || 0);
    const maxHeight = lineHeight * 3 + padding + border;
    const needed = el.scrollHeight + border;
    el.style.height = `${Math.min(needed, maxHeight)}px`;
    el.style.overflowY = needed > maxHeight ? "auto" : "hidden";
  }, [draft, status]);

  // Esc closes this popup only, not the whole Need Help panel.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  // The page behind the popup must not scroll. iOS Safari ignores
  // `overflow: hidden` on the body for touch scrolling, so the body is pinned
  // with position: fixed and the scroll position is restored on close.
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY;

    const previous = {
      htmlOverflow: html.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
    };

    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    // Phones/tablets: hide the page behind the chat while the keyboard shifts
    // the screen. The chat is portalled to <body>, outside #root.
    const ua = navigator.userAgent || "";
    const isTouchDevice =
      /Android|iPhone|iPad|iPod/i.test(ua) ||
      (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
    const rootEl = document.getElementById("root");
    const prevRootVisibility = rootEl ? rootEl.style.visibility : "";
    if (isTouchDevice && rootEl) rootEl.style.visibility = "hidden";

    return () => {
      html.style.overflow = previous.htmlOverflow;
      html.style.overscrollBehavior = previous.htmlOverscroll;
      body.style.overflow = previous.bodyOverflow;
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.left = previous.bodyLeft;
      body.style.right = previous.bodyRight;
      body.style.width = previous.bodyWidth;
      if (rootEl) rootEl.style.visibility = prevRootVisibility;
      window.scrollTo(0, scrollY);
    };
  }, []);

  // CHANGED: block touch / wheel scrolling on the overlay. Inside the message
  // list (and the textarea) a touch is only allowed when that box can really
  // scroll in the direction of the finger. At the top or bottom edge, or when
  // the box is not scrollable at all, the touch is cancelled. Older iPhone
  // Safari versions ignore `overscroll-behavior`, so without this the scroll
  // "chains" out of the list and the screen behind it moves.
  // Added by hand (not with React's onTouchMove) because it has to be a
  // non-passive listener to be allowed to call preventDefault.
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return undefined;

    let startY = 0;

    const getScroller = (target) => {
      if (!(target instanceof Element)) return null;
      if (listRef.current && listRef.current.contains(target)) {
        return listRef.current;
      }
      return target.closest("textarea");
    };

    const onTouchStart = (e) => {
      startY = e.touches[0] ? e.touches[0].clientY : 0;
    };

    const onTouchMove = (e) => {
      const scroller = getScroller(e.target);
      if (!scroller) {
        e.preventDefault();
        return;
      }
      const y = e.touches[0] ? e.touches[0].clientY : startY;
      const dy = y - startY; // > 0: finger moves down = scrolling up
      const canScroll = scroller.scrollHeight > scroller.clientHeight + 1;
      const atTop = scroller.scrollTop <= 0;
      const atBottom =
        scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;

      if (!canScroll || (dy > 0 && atTop) || (dy < 0 && atBottom)) {
        e.preventDefault();
      }
    };

    const onWheel = (e) => {
      if (getScroller(e.target)) return;
      e.preventDefault();
    };

    overlay.addEventListener("touchstart", onTouchStart, { passive: true });
    overlay.addEventListener("touchmove", onTouchMove, { passive: false });
    overlay.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      overlay.removeEventListener("touchstart", onTouchStart);
      overlay.removeEventListener("touchmove", onTouchMove);
      overlay.removeEventListener("wheel", onWheel);
    };
  }, []);

    // CHANGED: follow the visible area all the time (address bar showing or
  // hiding, keyboard opening or closing, rotation). The keyboard counts as
  // open when the visible height is clearly smaller than the window height OR
  // than the biggest visible height seen so far (needed on iPhone Safari).
  useEffect(() => {
    const viewport = window.visualViewport;

    const update = () => {
      // iOS scrolls the page when the keyboard opens; put it back.
      if (window.scrollY !== 0 || window.pageXOffset !== 0) {
        window.scrollTo(0, 0);
      }
      const next = readViewport();
      const width = viewport ? viewport.width : window.innerWidth;
      const base = baseRef.current;

      if (!base.height || Math.abs(base.width - width) > 1) {
        // first run, or the phone was rotated: start a new baseline
        baseRef.current = { height: next.height, width };
      } else if (next.height > base.height) {
        baseRef.current = { ...base, height: next.height };
      }

      const drop = Math.max(
        window.innerHeight - next.height,
        baseRef.current.height - next.height,
      );

      setView(next);
      setKeyboardOpen(drop > 150);
    };

    update();
    if (viewport) {
      viewport.addEventListener("resize", update);
      viewport.addEventListener("scroll", update);
    }
    window.addEventListener("resize", update);
    return () => {
      if (viewport) {
        viewport.removeEventListener("resize", update);
        viewport.removeEventListener("scroll", update);
      }
      window.removeEventListener("resize", update);
    };
  }, []);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending || status !== "ready") return;

    // Focus inside the tap, before any await, so the keyboard stays open on
    // iOS Safari.
    inputRef.current?.focus();

    setSending(true);
    setSendError("");
    try {
      const chat = await sendTicketChat({
        ticketId: ticket.ticketId,
        userId,
        message: text,
        token: getToken(),
      });
      // Shown only after the backend saved it.
      setChats((prev) => [...prev, chat]);
      setDraft("");
    } catch (_e) {
      setSendError("Your message wasn't sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    // Desktop (mouse + keyboard): Enter sends, Shift+Enter = new line.
    // Touch devices: Enter = new line, use the send button.
    if (e.key === "Enter" && !e.shiftKey) {
      const desktop =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(pointer: fine)").matches;
      if (desktop) {
        e.preventDefault();
        send();
      }
    }
  };

  // Follows the visible area, so the whole chat shrinks above the iPhone
  // keyboard. Everything else is in the CSS (.chatFull*).
  const overlayStyle = {
    top: view.top,
    bottom: "auto",
    height: view.height,
    overflow: "hidden",
    boxSizing: "border-box",
  };

  return createPortal(
    <div
      ref={overlayRef}
      className={`${styles.attachOverlay} ${styles.chatFullOverlay}`}
      style={overlayStyle}
      onClick={onClose}
    >
      {/* Card + close button below it (same as the Create Ticket popup) */}
      <div
        className={`${styles.popupStack} ${styles.chatFullStack}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`${styles.attachPopup} ${styles.chatPopup} ${styles.chatFullPopup} ${
            keyboardOpen ? styles.chatFullKb : ""
          }`}
          role="dialog"
          aria-modal="true"
          aria-label="Ticket chat"
        >
          <div className={`${styles.attachPopupHead} ${styles.chatFullHead}`}>
            <button
              type="button"
              className={styles.backBtn}
              onClick={onClose}
              aria-label="Back"
            >
              <svg
                width="9"
                height="15"
                viewBox="21 18.34 7.66 13.32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M21.293 25.7071C20.9025 25.3166 20.9025 24.6834 21.293 24.2929L26.9499 18.636C27.3404 18.2455 27.9736 18.2455 28.3641 18.636C28.7546 19.0266 28.7546 19.6597 28.3641 20.0503L23.4144 25L28.3641 29.9497C28.7546 30.3403 28.7546 30.9734 28.3641 31.364C27.9736 31.7545 27.3404 31.7545 26.9499 31.364L21.293 25.7071Z"
                  fill="#0A0A0A"
                  fillOpacity="0.9"
                />
              </svg>
            </button>
            <div className={styles.chatHeadText}>
              <h4 className={styles.attachPopupTitle}>{ticket.ticketId}</h4>
              {ticket.subject ? (
                <span className={styles.chatSubject}>{ticket.subject}</span>
              ) : null}
            </div>
            {/* same width as the back button, keeps the title centred */}
            <span className={styles.topbarSpacer} aria-hidden="true" />
          </div>

          <div ref={listRef} className={styles.chatList}>
            {status === "loading" && (
              <div className={styles.chatCenter}>
                <span className={styles.spinner} aria-label="Loading chat" />
              </div>
            )}

            {status === "error" && (
              <div className={styles.chatCenter}>
                <p className={styles.emptyText}>
                  We couldn't load this chat. Check your connection and try
                  again.
                </p>
                <button
                  type="button"
                  className={styles.centerBtn}
                  onClick={loadChats}
                >
                  Try again
                </button>
              </div>
            )}

            {status === "ready" && chats.length === 0 && (
              <div className={styles.chatCenter}>
                <p className={styles.emptyText}>
                  No messages yet. Send a message to start the conversation.
                </p>
              </div>
            )}

            {status === "ready" &&
              chats.map((m, i) => {
                const mine = m.sentBy === "user";
                return (
                  <div
                    key={`${m.timestamp}-${i}`}
                    className={`${styles.chatRow} ${
                      mine ? styles.chatRowMine : styles.chatRowTheirs
                    }`}
                  >
                    <div
                      className={`${styles.chatBubble} ${
                        mine ? styles.chatBubbleMine : styles.chatBubbleTheirs
                      }`}
                    >
                      {m.message}
                    </div>
                    <span className={styles.chatMeta}>
                      {!mine && "Support · "}
                      {formatTime(m.timestamp)}
                      {mine && ` · ${m.isRead ? "Seen" : "Sent"}`}
                    </span>
                  </div>
                );
              })}
          </div>

          {sendError && <p className={styles.chatError}>{sendError}</p>}

          <div className={styles.chatComposer}>
            <textarea
              ref={inputRef}
              className={styles.chatInput}
              placeholder="Type your message..."
              value={draft}
              maxLength={MAX_LEN}
              rows={1}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              // Not disabled while sending: a disabled textarea loses focus,
              // which closes the mobile keyboard.
              disabled={status !== "ready"}
              onFocus={() => {
                // iOS scrolls the focused field into view; undo that.
                setTimeout(() => window.scrollTo(0, 0), 100);
                setTimeout(() => window.scrollTo(0, 0), 350);
              }}
            />
            <button
              type="button"
              className={styles.chatSendBtn}
              // Keeps focus in the textarea when send is tapped.
              onMouseDown={(e) => e.preventDefault()}
              onClick={send}
              disabled={status !== "ready" || sending || !draft.trim()}
              aria-label="Send message"
            >
              <Send size={17} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        </div>

      </div>
    </div>,
    document.body,
  );
};

export default NeedHelpTicketChat;
